import { useCallback, useMemo } from "react";
import {
    ChartOfAccountsColumn,
    ChartOfAccountsView,
} from "@common/types/views/chartOfAccountsView";
import { NamedView } from "@common/types/views/views";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { ensureRequiredColumns } from "@hooks/view/ensureRequiredColumns";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { ViewConfig } from "@lib/views";
import { AccountingFiltersSetter } from "../useAccountingTab";
import { AccountingReportFilters } from "../types";
import {
    coaViewFiltersToPageFilters,
    pickCoaViewFilters,
} from "./chartOfAccountsViewFilters";
import {
    buildChartOfAccountsBuiltInViews,
    ChartOfAccountsBuiltInViewId,
} from "./chartOfAccountsBuiltInViews";
import { ChartOfAccountsRow } from "./utils";

interface Props {
    view: ChartOfAccountsView;
    defaultView: ChartOfAccountsView;
    onViewChange: (view: ChartOfAccountsView) => void;
    viewConfig: ViewConfig<ChartOfAccountsRow, ChartOfAccountsColumn, never>;
    filters: AccountingReportFilters;
    setFilters: AccountingFiltersSetter;
    /**
     * Embedded (drawer) use: starts on this view and leaves the page's cached
     * base view untouched.
     */
    initialBaseViewId?: ChartOfAccountsBuiltInViewId;
}

const REQUIRED_COLUMNS = [
    ChartOfAccountsColumn.NAME,
    ChartOfAccountsColumn.ACTIONS,
];

export const ChartOfAccountsViewToolbar: React.FC<Props> = ({
    view,
    defaultView,
    onViewChange,
    viewConfig,
    filters,
    setFilters,
    initialBaseViewId,
}) => {
    const builtInViews = useMemo<Array<NamedView<ChartOfAccountsView>>>(
        () => buildChartOfAccountsBuiltInViews(defaultView),
        [defaultView],
    );

    const persistView = useMemo<ChartOfAccountsView>(
        () => ({ ...view, filters: pickCoaViewFilters(filters) }),
        [view, filters],
    );

    const handleViewSelect = useCallback(
        (selected: ChartOfAccountsView) => {
            onViewChange({
                ...view,
                options: selected.options,
                columns: ensureRequiredColumns(
                    selected.columns,
                    defaultView.columns,
                    REQUIRED_COLUMNS,
                ),
            });
            setFilters((current) => ({
                ...current,
                ...coaViewFiltersToPageFilters(selected.filters),
            }));
        },
        [defaultView.columns, onViewChange, setFilters, view],
    );

    // Column edits keep the page filters; the picked view's filters apply
    // only through `onViewSelect`.
    const handleLayoutChange = useCallback(
        (changed: ChartOfAccountsView) => {
            onViewChange({
                ...view,
                columns: changed.columns,
                options: changed.options,
            });
        },
        [onViewChange, view],
    );

    return (
        <ViewToolbar<ChartOfAccountsView>
            view={persistView}
            onViewChange={handleLayoutChange}
            onViewSelect={handleViewSelect}
            viewConfig={viewConfig}
            builtInViews={builtInViews}
            defaultFilters={builtInViews[0].filters}
            baseViewCacheKey={
                initialBaseViewId === undefined
                    ? WORKSPACE_USER_CACHE_KEYS.CHART_OF_ACCOUNTS_PAGE_BASE_VIEW_ID_CACHE_KEY
                    : undefined
            }
            fallbackBaseViewId={initialBaseViewId}
        />
    );
};
